import type { ReactNode } from 'react';
import { T } from 'gt-react';
import { CaretDownIcon } from '@phosphor-icons/react';
import { Card } from 'src/components/ui/card';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from 'src/components/ui/collapsible';
import { TaskHelpRequestCard } from 'src/components/tasks/TaskHelpRequestCard';
import { TaskInputCard } from './TaskInputCard';
import { hasTaskInput } from './taskInputPresence';
import { TaskPhaseStepper } from './TaskPhaseStepper';
import { TaskAutomationCard } from './TaskAutomationCard';
import { SubtaskList } from './SubtaskList';
import { TaskTimeline } from './TaskTimeline';
import { TaskCommentsCard } from './TaskCommentsCard';
import { TaskReviewHeader } from './TaskReviewHeader';
import { useTaskComments } from 'src/pages/authenticated/inbox/hooks/use-task-comments';
import type { TaskReviewLoadedProps } from './TaskReviewLoadedProps';
import { taskTimelineViewProps } from './taskTimelineViewProps';
import {
  isRailSectionDefaultOpen,
  visibleRailSections,
  type TaskReviewRailSection,
} from './railSectionDefaults';

const RAIL_SECTION_TITLES: Record<TaskReviewRailSection, ReactNode> = {
  description: (
    <T context="collapsible heading for the task description in the monitor rail">
      Description
    </T>
  ),
  input: (
    <T context="collapsible heading for typed task input in the monitor rail">
      Input
    </T>
  ),
  progress: (
    <T context="collapsible heading for phase stepper and automation in the monitor rail">
      Progress
    </T>
  ),
  subtasks: (
    <T context="collapsible heading for a task's subtask list in the monitor rail">
      Subtasks
    </T>
  ),
  notes: (
    <T context="collapsible heading for notes on the task in the monitor rail, distinct from an embedded record's notes">
      Task Notes
    </T>
  ),
  timeline: (
    <T context="collapsible heading for the task audit timeline in the monitor rail, distinct from an embedded record's activity">
      Task Activity
    </T>
  ),
};

/** Parenthetical count, matching the AP invoice Activity / Notes tabs. */
function TabCount({ count }: { count: number | null }) {
  if (count === null || count === 0) return null;
  return <span className="tabular-nums"> ({count})</span>;
}

/**
 * Narrow stacked review for the agent-monitor Tasks rail. Header, help
 * request, and Task Activity stay open; the other sections start collapsed.
 */
export function TaskReviewRailLayout(props: TaskReviewLoadedProps) {
  const { task, subtasks, rollup, agentNameById, timeline } = props;
  const comments = useTaskComments(task.id);
  const notesCount = comments.isLoading
    ? null
    : (comments.data?.comments.length ?? 0);
  const activityCount = timeline.isLoading ? null : timeline.total;
  const sections = visibleRailSections({
    hasDescription: Boolean(task.description),
    hasInput: hasTaskInput(task.input),
    hasProgress: rollup.phase !== 'no_checklist',
    hasSubtasks: subtasks.length > 0,
  });

  return (
    <div className="flex min-h-0 flex-col gap-2">
      <TaskReviewHeader
        task={task}
        taskType={props.taskType}
        typeName={props.typeName}
        users={props.users}
        agents={props.agents}
        fromPage={props.fromPage}
        trailing={props.trailing}
      />

      <TaskHelpRequestCard task={task} agentNameById={agentNameById} />
      {subtasks
        .filter((subtask) => subtask.status === 'needs_input')
        .map((subtask) => (
          <TaskHelpRequestCard
            key={subtask.id}
            task={subtask}
            agentNameById={agentNameById}
          />
        ))}

      {sections.map((section) => (
        <RailSection
          key={section}
          section={section}
          count={
            section === 'notes'
              ? notesCount
              : section === 'timeline'
                ? activityCount
                : null
          }
        >
          {renderRailSection(section, props)}
        </RailSection>
      ))}
    </div>
  );
}

function RailSection({
  section,
  count = null,
  children,
}: {
  section: TaskReviewRailSection;
  count?: number | null;
  children: ReactNode;
}) {
  return (
    <Collapsible defaultOpen={isRailSectionDefaultOpen(section)}>
      <CollapsibleTrigger className="group flex w-full items-center gap-1 rounded-md px-1 py-1 text-left text-[11px] font-semibold uppercase tracking-[0.06em] text-stone-500 hover:text-stone-800">
        <CaretDownIcon className="size-3 transition-transform -rotate-90 group-data-[panel-open]:rotate-0" />
        {RAIL_SECTION_TITLES[section]}
        <TabCount count={count} />
      </CollapsibleTrigger>
      <CollapsibleContent className="pt-1">{children}</CollapsibleContent>
    </Collapsible>
  );
}

function renderRailSection(
  section: TaskReviewRailSection,
  props: TaskReviewLoadedProps,
): ReactNode {
  return RAIL_SECTION_BODIES[section](props);
}

const RAIL_SECTION_BODIES: Record<
  TaskReviewRailSection,
  (props: TaskReviewLoadedProps) => ReactNode
> = {
  description: ({ task }) => (
    <Card className="p-3">
      <div className="whitespace-pre-wrap text-sm text-stone-800">
        {task.description}
      </div>
    </Card>
  ),
  input: ({ task, taskType }) => (
    <TaskInputCard input={task.input} taskType={taskType} />
  ),
  progress: ({ rollup, typeNameByKey, task }) => (
    <div className="flex flex-col gap-2">
      <TaskPhaseStepper rollup={rollup} typeNameByKey={typeNameByKey} />
      <TaskAutomationCard taskId={task.id} typeNameByKey={typeNameByKey} />
    </div>
  ),
  subtasks: ({ subtasks, typeNameById, fromPage }) => (
    <SubtaskList
      subtasks={subtasks}
      typeNameById={typeNameById}
      fromPage={fromPage}
    />
  ),
  notes: ({ task, currentUserId, userNameById, agentNameById }) => (
    <TaskCommentsCard
      key={task.id}
      taskId={task.id}
      className="max-h-64"
      heading={null}
      currentUserId={currentUserId}
      userNameById={userNameById}
      agentName={
        task.assigneeAgentDefinitionId
          ? agentNameById.get(task.assigneeAgentDefinitionId)
          : undefined
      }
    />
  ),
  timeline: ({ task, timeline, userNameById, agentNameById }) => (
    <TaskTimeline
      {...taskTimelineViewProps({
        className: 'max-h-80',
        taskId: task.id,
        timeline,
        userNameById,
        agentNameById,
      })}
    />
  ),
};
